<template>
    <div class="vrm-modal vrm-modal--wide mfp-with-anim">
        <div class="vrm-modal__header">
            <div class="vrm-modal__title">
                {{
                    $t('groups.invite_new_members', { i18nFallback: 'Invite new members to group' })
                }}
            </div>
        </div>
        <div class="vrm-modal__content">
            <vrm-form @submit="onSubmit">
                <fieldset class="vrm-fieldset">
                    <div class="vrm-form__fieldset-item vrm-input-table">
                        <label class="sg-textfield-label sg-required">
                            <span class="sg-textfield-label-caption">
                                {{ $t('groups.invitations', { i18nFallback: 'Invitations' }) }}
                            </span>
                        </label>
                        <div v-if="!isMobile" class="vrm-table-grid__header">
                            <div>{{ $t('groups.name', { i18nFallback: 'Name' }) }}</div>
                            <div>{{ $t('common.email', { i18nFallback: 'Email' }) }}</div>
                            <div>{{ $t('users.role', { i18nFallback: 'Role' }) }}</div>
                        </div>
                        <div
                            v-for="(member, index) in invites"
                            :key="member._key"
                            class="vrm-table-grid__body"
                        >
                            <div>
                                <div v-if="isMobile" class="vrm-table-grid__body__label">
                                    {{ $t('groups.name', { i18nFallback: 'Name' }) }}
                                </div>
                                <vrm-text-field
                                    v-model="member.name"
                                    type="text"
                                    class="sg-element"
                                    :checks="[CANNOT_CONTAIN_URL]"
                                    :disabled="busy"
                                />
                            </div>
                            <div>
                                <div v-if="isMobile" class="vrm-table-grid__body__label">
                                    {{ $t('common.email', { i18nFallback: 'Email' }) }}
                                </div>
                                <vrm-text-field
                                    v-model="member.email"
                                    type="text"
                                    class="sg-element"
                                    placeholder="name@example.com"
                                    :checks="[VALID_EMAIL_OR_EMPTY]"
                                    :disabled="busy"
                                    :hide-validation-message-on-focus="true"
                                    style="margin-top: 0;"
                                />
                            </div>
                            <div>
                                <div v-if="isMobile" class="vrm-table-grid__body__label">
                                    {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                </div>
                                <vrm-dropdown
                                    v-model="member.accessLevelOption"
                                    :options="accessLevelDropdownOptions"
                                />
                            </div>
                            <div v-if="isMobile">
                                <span
                                    v-if="
                                        (isMobile && index > 0) || (isMobile && invites.length > 1)
                                    "
                                    class="vrm-table__icon--link-mobile-remove"
                                    :disabled="busy"
                                    @click="removeInviteRow(index)"
                                >
                                    <i class="sg-vrm-icon_151_trash_24px" />
                                    <span>
                                        {{
                                            $t('groups.remove_new_member', {
                                                i18nFallback: 'Remove member',
                                            })
                                        }}
                                    </span>
                                </span>
                                <span
                                    v-if="isMobile && index === invites.length - 1"
                                    class="vrm-table__icon--link-mobile-add"
                                    :disabled="busy"
                                    @click="addInviteRow"
                                >
                                    <i class="sg-vrm-icon_088_plus_12px" />
                                    <span>
                                        {{
                                            $t('groups.add_new_member', {
                                                i18nFallback: 'Add a new member',
                                            })
                                        }}
                                    </span>
                                </span>
                            </div>
                            <span
                                v-if="!isMobile && index !== invites.length - 1"
                                class="vrm-table__icon--link"
                                :disabled="busy"
                                @click="removeInviteRow(index)"
                            >
                                <i class="sg-vrm-icon_151_trash_24px" />
                            </span>
                        </div>
                    </div>
                    <vrm-text-area
                        v-model="personalMessage"
                        :title="
                            $t('groups.invitation_message', { i18nFallback: 'Invitation message' })
                        "
                        :checks="[CANNOT_CONTAIN_URL]"
                    />
                </fieldset>
                <fieldset class="vrm-fieldset" :class="{ 'vrm-table-grid__footer': isMobile }">
                    <div class="sg-spread">
                        <button
                            type="reset"
                            class="sg-btn sg-site"
                            :disabled="busy"
                            @click="onReset"
                        >
                            {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
                        </button>
                        <button
                            type="submit"
                            class="sg-btn sg-blue"
                            :class="{ 'sg-disabled': areInvitesInvalid }"
                            :disabled="busy || areInvitesInvalid"
                        >
                            {{ $t('groups.send_invites', { i18nFallback: 'Send invitations' }) }}
                        </button>
                    </div>
                </fieldset>
            </vrm-form>
        </div>
        <modal-close-handle />
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import {
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATIONS,
    VrmNotification,
} from '@/vue/store/modules/notifications';
import { ngInject } from '@/vue/decorators/angular-interop';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmTextArea from '@/vue/components/forms/VrmTextArea';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import VrmForm from '@/vue/components/forms/VrmForm';
import { CANNOT_CONTAIN_URL, VALID_EMAIL_OR_EMPTY } from '@/vue/constants/validation-checks';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import {
    accessLevelDropdownOptions,
    findAccessLevelOption,
    vrmInstallationAccessLevel,
} from '@/vue/store/modules/installation-access-level';

@Component({
    components: { VrmForm, VrmTextField, ModalCloseHandle, VrmTextArea, VrmDropdown },
})
@AngularLocaleBundle('common')
@AngularLocaleBundle('groups')
@AngularLocaleBundle('users')
export default class InstallationGroupLinkUsersModal extends Vue {
    CANNOT_CONTAIN_URL = CANNOT_CONTAIN_URL;
    VALID_EMAIL_OR_EMPTY = VALID_EMAIL_OR_EMPTY;
    MAX_INVITES = 10;

    @Prop({ type: Object })
    group;

    @ngInject()
    InstallationGroupsService;

    invites = [];
    personalMessage = '';
    busy = false;
    accessLevelDropdownOptions = accessLevelDropdownOptions;
    isMobile = window.innerWidth < 1280;

    mounted() {
        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
        this.isMobile = window.innerWidth < 1280;
    }

    get lastItem() {
        if (!this.invites.length) return null;
        const item = this.invites[this.invites.length - 1];
        return item.name || item.email;
    }

    @Watch('lastItem', { immediate: true })
    onLastItemFilled(value) {
        if (this.isMobile && this.invites.length >= 1) return;
        if ((value && this.invites.length < this.MAX_INVITES) || !this.invites.length) {
            this.invites.push({
                _key: Date.now() + Math.random(), // Unique key for Vue reactivity
                name: '',
                email: '',
                accessLevelOption: findAccessLevelOption(vrmInstallationAccessLevel.MONITORING),
            });
        }
    }

    addInviteRow() {
        if (this.invites.length < this.MAX_INVITES) {
            this.invites.push({
                _key: Date.now() + Math.random(), // Unique key for Vue reactivity
                name: '',
                email: '',
                accessLevelOption: findAccessLevelOption(vrmInstallationAccessLevel.MONITORING),
            });
        }
    }

    removeInviteRow(index) {
        this.invites.splice(index, 1);
    }

    get areInvitesInvalid() {
        if (this.isMobile) {
            return (
                this.validInvites.length === 0 || this.validInvites.length !== this.invites.length
            );
        } else {
            return (
                this.validInvites.length === 0 ||
                this.validInvites.length !== this.invites.length - 1
            );
        }
    }

    get validInvites() {
        return this.invites
            .filter(item => item.email && item.email.trim() !== '')
            .map(item => ({
                name: item.name,
                email: item.email,
                accessLevel: item.accessLevelOption.accessLevel,
            }));
    }

    async onSubmit() {
        if (this.busy) return;
        this.busy = true;
        try {
            const payload = {
                invites: this.validInvites,
                inviteMessage: this.personalMessage,
            };
            await this.InstallationGroupsService.inviteUsersToGroup(
                this.group.idSiteGroup,
                payload,
            );
            this.$store.dispatch(NOTIFICATIONS.ADD, {
                notification: new VrmNotification({
                    type: NOTIFICATION_TYPE_SUCCESS,
                    title: this.$t('groups.invites_sent', { i18nFallback: 'Invites sent' }),
                }),
            });
            this.$emit('close');
        } finally {
            this.busy = false;
        }
    }

    onReset() {
        this.$emit('close');
    }
}
</script>
